import { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { css } from '@emotion/css';

import { subjectContents, type SubjectKey } from '@/archive/constants/subject';
import { subjectStructuredDataByKey } from '@/constants/jsonld/subjects';

import DefaultPageLayout from '@/archive/components/templates/DefaultPageLayout';
import { JsonLd } from '@/features/common/components';
import { service } from '@/features/common/configs';
import PrepareContent from '@/features/subject/prepare/PrepareContent';
import KoreanContent from '@/features/subject/kor/KoreanContent';
import MathContent from '@/features/subject/math/MathContent';
import EnglishContent from '@/features/subject/en/EnglishContent';
import ChoiceContent from '@/features/subject/choice/ChoiceContent';
import { defaultImageContainer, nextButtonContainerStyle } from '@/styles/mixins';

const AnimationImageSection = () => css`
    width: 100%;
    position: absolute;
    top: 89.9%;
    display: flex;
    justify-content: center;
    gap: 22px;
`;

const AnimationImageWrapper = () => css`
    background-color: #ffffff;
    border-radius: 20px;
    box-shadow: 0 4px 0 #dbdbdb;
    height: 132px;
    > img {
        width: 230px;
        border-radius: 20px;
    }
`;

const isValidSubjectKey = (subjectKey?: string | string[]): subjectKey is SubjectKey => {
    if (typeof subjectKey !== 'string') {
        return false;
    }

    return Object.keys(subjectContents).includes(subjectKey);
};

export const getServerSideProps = (context: GetServerSidePropsContext) => {
    const subjectKey = context.query.subjectKey;

    if (!isValidSubjectKey(subjectKey)) {
        return {
            redirect: {
                permanent: false,
                destination: '/subject/kor',
            },
            props: {
                subjectKey: 'kor',
            },
        };
    }

    return { props: { subjectKey } };
};

interface Props {
    subjectKey: SubjectKey;
}

const SubjectPage = ({ subjectKey }: Props) => {
    const subjectContent = subjectContents[subjectKey];
    const structuredData = subjectStructuredDataByKey[subjectKey];

    const renderAniImg = (aniImgConfig?: { index: number; url: string; className: string }[] | undefined) => {
        if (!aniImgConfig) return null;
        return (
            <div className={AnimationImageSection()}>
                {aniImgConfig.map((value: any) => {
                    return (
                        <div key={value.className} className={AnimationImageWrapper()}>
                            <img src={value.url} alt={value.alt || ''} />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            {structuredData && <JsonLd id={`wink-subject-${subjectKey}-jsonld`} data={structuredData} />}
            <Head>
                {subjectKey === 'prepare' && (
                    <>
                        <title>초등교과(캐츠홈) | 윙크</title>
                        <meta property="og:title" content="초등교과(캐츠홈) | 윙크" />
                    </>
                )}
                <meta property="og:description" itemProp="description" content={subjectContent.description} />
                <meta property="og:image" content={subjectContent.metaImage} />
                <meta name="description" content={subjectContent.description} />
            </Head>
            {subjectKey === 'kor' ? (
                <KoreanContent />
            ) : subjectKey === 'math' ? (
                <MathContent />
            ) : subjectKey === 'en' ? (
                <EnglishContent />
            ) : subjectKey === 'choice' ? (
                <ChoiceContent />
            ) : subjectKey === 'prepare' ? (
                <PrepareContent />
            ) : (
                <DefaultPageLayout
                    className={css`
                        width: 100%;
                    `}
                    pageTitle={subjectContent.title}
                >
                    {renderAniImg(subjectContent?.aniImgValues)}
                    {Array.from({ length: subjectContent.imgCount }).map((item, idx) => {
                        const src = service.getRecentModifiedImageUrl(idx + 1, subjectContent);
                        if (subjectContent?.skipContents?.find((value) => value === idx + 1)) {
                            return;
                        }

                        return (
                            <div key={idx} className={defaultImageContainer(subjectContent.backgroundColors[idx])}>
                                <img src={src} alt={`${subjectKey}-${idx}`} />
                            </div>
                        );
                    })}
                    {subjectContent.nextButton && (
                        <div className={nextButtonContainerStyle}>
                            <Link id={`subject-${subjectKey}-next-button`} href={subjectContent.nextButton.to}>
                                <img src={subjectContent.nextButton.imageUrl} alt={'next-button'} />
                            </Link>
                        </div>
                    )}
                </DefaultPageLayout>
            )}
        </>
    );
};

export default SubjectPage;
